import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../api.js';
import { useAuthStore } from '../store/auth.js';
import { useT } from '../i18n/useT.js';
import { useAlert } from '../components/AlertProvider.jsx';
import { Plus, Pencil, Trash2, Layers, BadgeCheck, CreditCard, X, Monitor, Code2, Rocket, Palette, Power, Sparkles } from 'lucide-react';

const LIMIT_KEYS = [
  { key: 'rendersMonth', i18n: 'billing.rendersMonth' },
  { key: 'concurrentRenders', i18n: 'billing.concurrentRenders' },
  { key: 'storageGB', i18n: 'billing.storageGB' },
  { key: 'apiCallsPerMinute', i18n: 'billing.apiCallsPerMinute' },
  { key: 'templatesActive', i18n: 'billing.templatesActive' },
  { key: 'templateVersionsHistory', i18n: 'billing.templateVersionsHistory' },
  { key: 'aiGenerationsMonth', i18n: 'billing.aiGenerationsMonth' },
];

const FEATURE_KEYS = [
  { key: 'studio', i18n: 'billing.studio' },
  { key: 'visualBuilder', i18n: 'billing.visualBuilder' },
  { key: 'apiAccess', i18n: 'billing.apiAccess' },
  { key: 'priorityQueue', i18n: 'billing.priorityQueue' },
  { key: 'customBranding', i18n: 'billing.customBranding' },
  { key: 'aiGeneration', i18n: 'billing.aiGeneration' },
];

const BILLING_MODELS = [
  { value: 'manual', i18n: 'billing.planModelManual', fallback: 'Manual' },
  { value: 'tiered', i18n: 'billing.planModelTiered', fallback: 'Tiered' },
  { value: 'enterprise', i18n: 'billing.planModelEnterprise', fallback: 'Enterprise' },
];

const emptyPlan = {
  code: '',
  name: '',
  description: '',
  monthlyPrice: 0,
  annualEnabled: false,
  annualDiscountPercent: 0,
  temporaryDiscountPercent: 0,
  temporaryDiscountLabel: '',
  temporaryDiscountStartsAt: '',
  temporaryDiscountEndsAt: '',
  currency: 'USD',
  billingModel: 'manual',
  includedLimits: {
    rendersMonth: 10000,
    concurrentRenders: 3,
    storageGB: 5,
    apiCallsPerMinute: 60,
    templatesActive: 50,
    templateVersionsHistory: 10,
    aiGenerationsMonth: 50,
  },
  features: {
    studio: true,
    visualBuilder: true,
    apiAccess: true,
    priorityQueue: false,
    customBranding: false,
    aiGeneration: true,
  },
  isActive: true,
};

function toDateTimeInput(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return localDate.toISOString().slice(0, 16);
}

function toIsoOrNull(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export default function PlansPage() {
  const user = useAuthStore((s) => s.user);
  const t = useT();
  const { showAlert, showConfirm } = useAlert();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | planId
  const [form, setForm] = useState({ ...emptyPlan });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const planStats = useMemo(() => {
    const activePlans = plans.filter((plan) => plan.isActive).length;
    const paidPlans = plans
      .filter((plan) => plan.isActive && Number(plan.monthlyPrice || 0) > 0)
      .map((plan) => ({ amount: Number(plan.monthlyPrice || 0), currency: plan.currency || 'USD' }));
    const startingPrice = paidPlans.length ? Math.min(...paidPlans.map((item) => item.amount)) : 0;
    const startingCurrency = paidPlans.find((item) => item.amount === startingPrice)?.currency || 'USD';

    return {
      total: plans.length,
      active: activePlans,
      startingPrice,
      startingCurrency,
    };
  }, [plans]);

  if (String(user?.role || '').toLowerCase() !== 'admin') {
    return <Navigate to="/templates" replace />;
  }

  const loadPlans = async () => {
    setLoading(true);
    try {
      const res = await api.getBillingPlans();
      setPlans(res.items || []);
    } catch { setPlans([]); }
    setLoading(false);
  };

  useEffect(() => { loadPlans(); }, []);

  const startCreate = () => {
    setForm({ ...emptyPlan });
    setEditing('new');
    setError('');
  };

  const startEdit = (plan) => {
    setForm({
      code: plan.code,
      name: plan.name,
      description: plan.description || '',
      monthlyPrice: plan.monthlyPrice,
      annualEnabled: Boolean(plan.annualEnabled),
      annualDiscountPercent: Number(plan.annualDiscountPercent || 0),
      temporaryDiscountPercent: Number(plan.temporaryDiscountPercent || 0),
      temporaryDiscountLabel: plan.temporaryDiscountLabel || '',
      temporaryDiscountStartsAt: toDateTimeInput(plan.temporaryDiscountStartsAt),
      temporaryDiscountEndsAt: toDateTimeInput(plan.temporaryDiscountEndsAt),
      currency: plan.currency,
      billingModel: plan.billingModel,
      includedLimits: { ...emptyPlan.includedLimits, ...plan.includedLimits },
      features: { ...emptyPlan.features, ...plan.features },
      isActive: plan.isActive,
    });
    setEditing(plan.id);
    setError('');
  };

  const cancelEdit = () => {
    setEditing(null);
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        temporaryDiscountStartsAt: toIsoOrNull(form.temporaryDiscountStartsAt),
        temporaryDiscountEndsAt: toIsoOrNull(form.temporaryDiscountEndsAt),
      };
      if (editing === 'new') {
        await api.createBillingPlan(payload);
      } else {
        await api.updateBillingPlan(editing, payload);
      }
      setEditing(null);
      await loadPlans();
    } catch (err) {
      setError(err.message || t('billing.planSaveFailed', 'Could not save plan.'));
    }
    setSaving(false);
  };

  const handleDelete = async (plan) => {
    const accepted = await showConfirm(t('billing.confirmDeletePlan', `Delete plan "${plan.name}"?`, { name: plan.name }), {
      title: t('billing.deletePlan', 'Delete Plan'),
      confirmLabel: t('billing.yes', 'Yes'),
      cancelLabel: t('billing.cancel', 'Cancel'),
      tone: 'warning',
    });
    if (!accepted) return;
    try {
      await api.deleteBillingPlan(plan.id);
      await loadPlans();
    } catch (err) {
      showAlert(err.message || t('billing.planDeleteFailed', 'Could not delete plan.'), { tone: 'error', title: t('billing.loadFailed', 'Failed') });
    }
  };

  const setLimit = (key, val) => {
    setForm((prev) => ({
      ...prev,
      includedLimits: { ...prev.includedLimits, [key]: Number(val) || 0 },
    }));
  };

  const setFeature = (key, val) => {
    setForm((prev) => ({
      ...prev,
      features: { ...prev.features, [key]: val },
    }));
  };

  const modelLabel = (value) => {
    const model = BILLING_MODELS.find((item) => item.value === value);
    if (!model) return value;
    return t(model.i18n, model.fallback);
  };

  const formatLimit = (value) => {
    if (value === -1) return t('billing.unlimited', 'Unlimited');
    return Number(value || 0).toLocaleString();
  };

  const enabledFeaturesCount = (features = {}) =>
    FEATURE_KEYS.reduce((sum, feature) => sum + (features[feature.key] ? 1 : 0), 0);

  const featurePreview = (features = {}) => FEATURE_KEYS.map((feature) => Boolean(features[feature.key]));

  const featureCards = [
    { key: 'studio', icon: Monitor, title: t('billing.studio', 'Studio Access'), desc: t('billing.featureStudioDesc', 'Full visual editor & preview') },
    { key: 'visualBuilder', icon: Monitor, title: t('billing.visualBuilder', 'Visual Builder'), desc: t('billing.featureVisualBuilderDesc', 'Drag-and-drop visual template builder') },
    { key: 'apiAccess', icon: Code2, title: t('billing.apiAccess', 'API Access'), desc: t('billing.featureApiDesc', 'Programmatic rendering via REST') },
    { key: 'priorityQueue', icon: Rocket, title: t('billing.priorityQueue', 'Priority Queue'), desc: t('billing.featurePriorityDesc', 'Jobs rendered first, no wait') },
    { key: 'customBranding', icon: Palette, title: t('billing.customBranding', 'Custom Branding'), desc: t('billing.featureBrandingDesc', 'Remove watermarks, use your logo') },
    { key: 'aiGeneration', icon: Sparkles, title: t('billing.aiGeneration', 'AI Generation'), desc: t('billing.featureAiDesc', 'Generate templates and scripts via AI') },
  ];

  if (loading) return <div className="page-container"><p>{t('billing.loading', 'Loading...')}</p></div>;

  return (
    <div className="page-container plans-page plans-v2">
      <div className="plans-v2__container">
      <div className="page-header plans-page__header">
        <div>
          <h1 className="plans-page__title-wrap"><span className="plans-page__title-icon"><CreditCard size={14} /></span>{t('billing.plansTitle', 'Billing Plans')}</h1>
          <p className="plans-page__subtitle">
            {t('billing.plansSubtitle', 'Manage plan definitions, limits, and features.')}
          </p>
        </div>
        <button className="btn btn-primary plans-page__create" onClick={startCreate}>
          <Plus size={16} /> {t('billing.createPlan', 'Create Plan')}
        </button>
      </div>

      <div className="plans-page__stats">
        <div className="plans-stat-card">
          <div className="plans-stat-card__label"><span className="plans-stat-card__icon"><Layers size={14} /></span> {t('billing.plansStatTotal', 'Total plans')}</div>
          <div className="plans-stat-card__value">{planStats.total}</div>
        </div>
        <div className="plans-stat-card">
          <div className="plans-stat-card__label"><span className="plans-stat-card__icon"><BadgeCheck size={14} /></span> {t('billing.plansStatActive', 'Active plans')}</div>
          <div className="plans-stat-card__value">{planStats.active}</div>
        </div>
        <div className="plans-stat-card">
          <div className="plans-stat-card__label"><span className="plans-stat-card__icon"><CreditCard size={14} /></span> {t('billing.plansStatStartingAt', 'Starting at')}</div>
          <div className="plans-stat-card__value">{t('billing.priceAmount', '{{currency}} {{amount}}', { currency: planStats.startingCurrency, amount: planStats.startingPrice.toFixed(2) })}</div>
        </div>
      </div>

      {editing && (
        <section className="card plans-editor">
          <div className="plans-editor__head">
            <h3 className="plans-editor__title">
              {editing === 'new' ? t('billing.createPlanTitle', 'Create New Plan') : t('billing.editPlan', 'Edit Plan')}
            </h3>
            <button className="btn btn-icon" onClick={cancelEdit} title={t('billing.close', 'Close')}>
              <X size={14} />
            </button>
          </div>

          {error && <div className="plans-editor__error">{error}</div>}

          <h4 className="plans-editor__section-label">{t('billing.planSectionBasic', 'Basic Info')}</h4>
          <div className="plans-editor__grid plans-editor__grid--two">
            <div className="form-group">
              <label>{t('billing.planCode', 'Code')}</label>
              <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                disabled={editing !== 'new'} placeholder={t('billing.planCodePlaceholder', 'e.g. pro')} />
            </div>
            <div className="form-group">
              <label>{t('billing.planName', 'Name')}</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
          </div>

          <div className="form-group plans-editor__block-space">
            <label>{t('billing.planDescription', 'Description')}</label>
            <textarea value={form.description} rows={2}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t('billing.planDescriptionPlaceholder', 'Short description for customers...')} />
          </div>

          <h4 className="plans-editor__section-label">{t('billing.planSectionPricing', 'Pricing')}</h4>
          <div className="plans-editor__grid plans-editor__grid--three">
            <div className="form-group">
              <label>{t('billing.planPrice', 'Monthly Price')}</label>
              <input type="number" min={0} step={0.01} value={form.monthlyPrice}
                onChange={(e) => setForm({ ...form, monthlyPrice: parseFloat(e.target.value) || 0 })} />
            </div>
            <div className="form-group">
              <label>{t('billing.annualBillingEnabled', 'Annual Billing Enabled')}</label>
              <select value={form.annualEnabled ? 'yes' : 'no'}
                onChange={(e) => setForm({ ...form, annualEnabled: e.target.value === 'yes' })}>
                <option value="no">{t('billing.no', 'No')}</option>
                <option value="yes">{t('billing.yes', 'Yes')}</option>
              </select>
            </div>
            <div className="form-group">
              <label>{t('billing.annualDiscountPercent', 'Annual Discount %')}</label>
              <input type="number" min={0} max={100} step={0.01} value={form.annualDiscountPercent}
                onChange={(e) => setForm({ ...form, annualDiscountPercent: parseFloat(e.target.value) || 0 })}
                disabled={!form.annualEnabled} />
            </div>
          </div>

          <div className="plans-editor__grid plans-editor__grid--three plans-editor__block-space">
            <div className="form-group">
              <label>{t('billing.planCurrency', 'Currency')}</label>
              <input value={form.currency} maxLength={3}
                onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} />
            </div>
            <div className="form-group">
              <label>{t('billing.temporaryDiscountPercent', 'Temporary Discount %')}</label>
              <input type="number" min={0} max={100} step={0.01} value={form.temporaryDiscountPercent}
                onChange={(e) => setForm({ ...form, temporaryDiscountPercent: parseFloat(e.target.value) || 0 })} />
            </div>
            <div className="form-group">
              <label>{t('billing.planModel', 'Billing Model')}</label>
              <select value={form.billingModel}
                onChange={(e) => setForm({ ...form, billingModel: e.target.value })}>
                {BILLING_MODELS.map((model) => (
                  <option key={model.value} value={model.value}>{t(model.i18n, model.fallback)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="plans-editor__grid plans-editor__grid--three plans-editor__block-space">
            <div className="form-group">
              <label>{t('billing.temporaryDiscountLabel', 'Temporary Discount Label')}</label>
              <input value={form.temporaryDiscountLabel}
                onChange={(e) => setForm({ ...form, temporaryDiscountLabel: e.target.value })}
                placeholder={t('billing.temporaryDiscountLabelPlaceholder', 'Launch promo, Summer sale...')} />
            </div>
            <div className="form-group">
              <label>{t('billing.temporaryDiscountStartsAt', 'Discount Starts At')}</label>
              <input type="datetime-local" value={form.temporaryDiscountStartsAt}
                onChange={(e) => setForm({ ...form, temporaryDiscountStartsAt: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t('billing.temporaryDiscountEndsAt', 'Discount Ends At')}</label>
              <input type="datetime-local" value={form.temporaryDiscountEndsAt}
                onChange={(e) => setForm({ ...form, temporaryDiscountEndsAt: e.target.value })} />
            </div>
          </div>

          <div className="plans-editor__hint plans-editor__block-space">
            {form.monthlyPrice > 0
              ? `${form.currency} ${Number(form.monthlyPrice || 0).toFixed(2)} / mo${form.annualEnabled ? ` · ${form.currency} ${(Number(form.monthlyPrice || 0) * 12 * (1 - (Number(form.annualDiscountPercent || 0) / 100))).toFixed(2)} / yr` : ''}`
              : t('billing.free', 'Free')}
          </div>

          <h4 className="plans-editor__section-label">{t('billing.planLimits', 'Limits')}</h4>
          <div className="plans-editor__grid plans-editor__grid--auto">
            {LIMIT_KEYS.map(({ key, i18n }) => (
              <div key={key} className="form-group plans-editor__limit-box">
                <label style={{ fontSize: 12 }}>{t(i18n, key)}</label>
                <input type="number" value={form.includedLimits[key] ?? 0}
                  onChange={(e) => setLimit(key, e.target.value)} />
                <span className="plans-editor__hint">
                  {form.includedLimits[key] === -1 ? t('billing.unlimited', 'Unlimited') : t('billing.unlimitedHint', 'Use -1 for unlimited')}
                </span>
              </div>
            ))}
          </div>

          <h4 className="plans-editor__section-label">{t('billing.planFeaturesStatus', 'Features & Status')}</h4>
          <div className="plans-editor__grid plans-editor__grid--feature-cards plans-editor__block-space">
            {featureCards.map(({ key, icon: Icon, title, desc }) => (
              <button
                key={key}
                type="button"
                className={`plans-feature-card ${form.features[key] ? 'is-enabled' : ''}`}
                onClick={() => setFeature(key, !form.features[key])}
              >
                <span className="plans-feature-card__icon"><Icon size={14} /></span>
                <div className="plans-feature-card__content">
                  <strong>{title}</strong>
                  <span>{desc}</span>
                </div>
                <span className={`plans-feature-card__check ${form.features[key] ? 'is-on' : ''}`} />
              </button>
            ))}
          </div>

          <div className="plans-editor__status-toggle plans-editor__block-space">
            <div className="plans-editor__status-info">
              <span className="plans-editor__status-icon"><Power size={14} /></span>
              <div>
                <strong>{t('billing.planActive', 'Plan Active')}</strong>
                <p>{t('billing.planActiveHint', 'Visible and available to subscribers')}</p>
              </div>
            </div>
            <label className="switch">
              <input type="checkbox" checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              <span className="slider" />
            </label>
          </div>

          <div className="plans-editor__actions">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? t('billing.saving', 'Saving...') : t('billing.save', 'Save')}
            </button>
            <button className="btn" onClick={cancelEdit}>{t('billing.cancel', 'Cancel')}</button>
          </div>
        </section>
      )}

      <div className="card plans-table-wrap">
        <div className="plans-table-wrap__head">
          <h3>{t('billing.allPlans', 'All Plans')}</h3>
          <span className="plans-table-wrap__count">{t('billing.plansCount', '{{count}} plans', { count: plans.length })}</span>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('billing.planCode', 'Code')}</th>
              <th>{t('billing.planName', 'Name')}</th>
              <th>{t('billing.planPrice', 'Monthly Price')}</th>
              <th>{t('billing.planModel', 'Billing Model')}</th>
              <th>{t('billing.templateVersionsHistory', 'Template versions')}</th>
              <th>{t('billing.planFeatures', 'Features')}</th>
              <th>{t('billing.status', 'Status')}</th>
              <th>{t('billing.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => (
              <tr key={plan.id}>
                <td><code>{plan.code}</code></td>
                <td>
                  <div className="plans-table__name">{plan.name}</div>
                  <div className="plans-table__description">{plan.description || t('billing.planDescriptionEmpty', 'No description')}</div>
                </td>
                <td className="plans-table__price">
                  {Number(plan.monthlyPrice || 0) > 0 ? (
                    <>
                      <div>{t('billing.priceAmountMonthly', '{{currency}} {{amount}} / mo', { currency: plan.currency, amount: Number(plan.monthlyPrice || 0).toFixed(2) })}</div>
                      {plan.pricing?.annual && (
                        <div className="plans-table__description">{plan.currency} {Number(plan.pricing.annual.amount || 0).toFixed(2)} / yr</div>
                      )}
                      {plan.pricing?.temporaryDiscount?.isActive && (
                        <div className="plans-table__description">{plan.temporaryDiscountLabel || 'Promo'} · -{Number(plan.temporaryDiscountPercent || 0)}%</div>
                      )}
                    </>
                  ) : t('billing.free', 'Free')}
                </td>
                <td><span className={`plans-model-badge is-${plan.billingModel}`}>{modelLabel(plan.billingModel)}</span></td>
                <td>{formatLimit(plan.includedLimits?.templateVersionsHistory)}</td>
                <td>
                  <div className="plans-feature-dots">
                    {featurePreview(plan.features || {}).map((enabled, index) => (
                      <span key={`${plan.id}_${index}`} className={`plans-feature-dot ${enabled ? 'is-on' : ''}`} />
                    ))}
                    <span className="plans-feature-dots__count">{enabledFeaturesCount(plan.features || {})}/{FEATURE_KEYS.length}</span>
                  </div>
                </td>
                <td>
                  <span className={`plans-status-pill ${plan.isActive ? 'is-active' : 'is-inactive'}`}>
                    {plan.isActive ? t('billing.planActive', 'Active') : t('billing.planInactive', 'Inactive')}
                  </span>
                </td>
                <td>
                  <div className="plans-table__actions">
                    <button className="btn btn-icon" title={t('billing.editPlan', 'Edit Plan')}
                      onClick={() => startEdit(plan)}>
                      <Pencil size={14} />
                    </button>
                    <button className="btn btn-icon btn-danger" title={t('billing.deletePlan', 'Delete Plan')}
                      onClick={() => handleDelete(plan)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {plans.length === 0 && (
              <tr><td colSpan={8} className="plans-table__empty">{t('billing.noPlan', 'No plans found.')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}
